import { BaseModel } from "./baseModel";
import {
  IResponseElementWebhook,
  IResponseElementText,
  IResponseElementImage,
  IResponseElementVideo,
  IResponseElementSetAttribute,
  IResponseElementCard,
  IResponseElementQuestion,
  IResponseElementGoto,
  IResponseElementAdaptiveCard,
  IResponseElementRoomRate,
  IResponseElementBookingConfirmation,
  IResponseElementBookingCancellation,
  IResponseElementHotelInfo,
  IResponseElementSetRecognizerScope,
  IResponseElementGuiaMedico,
  IResponseElementGuiasBeneficiario,
  IResponseElementStatusGuia,
  IResponseElementPrintScreen,
  IResponseElementRating,
  IResponseElementEndConversation,
  IResponseElementMedicalAppointment,
  IResponseElementTemplate,
  IResponseElementComment,
  IResponseElementBDMedicalScale,
  IResponseElementBDResetMedicalAppointment,
  IResponseElementReassignConversation,
  IResponseElementPrivacyPolicy,
  IResponseElementBDPatientRecoverPassword,
} from "./responseElement";

export enum InteractionType {
  welcome = "welcome",
  fallback = "fallback",
  contextFallback = "context-fallback",
  interaction = "interaction",
}

export enum ConditionMethodType {
  equals = "equals",
  not_equal = "not-equals",
  empty = "empty",
  contains = "contains",
  not_contains = "not-contains",
  greater_than = "greater-than",
  less_than = "less-than",
  greater_than_or_equal = "greater-than-or-equal",
}

export enum OperatorType {
  OR = "or",
  AND = "and",
}

export enum ConditionType {
  attribute = "attribute",
  lifespan = "lifespan",
  recognizerScore = "recognizer-score",
}

export enum RecognizerType {
  ai = "ai",
  keyword = "keyword",
}

export enum ResponseType {
  //default responses
  TEXT = "text",
  CARD = "card",
  GOBACK = "go-back",
  CAROUSEL = "carousel",
  QUESTION = "question",
  GOTO = "goto",
  POST_BACK = "postback",
  SET_ATTRIBUTE = "set-attribute",
  SEND_EMAIL = "send-email",
  WEBHOOk = "webhook",
  END = "end",
  QUICK_REPLY = "quick-reply",
  IMAGE = "image",
  VIDEO = "video",
  TAGS = "tags",
  SET_RECOGNIZER_SCOPE = "set-recognizer-scope",
  ADAPTIVE_CARD = "AdaptiveCard",
  SWITCH_TEXT = "switch-text",
  FAQ = "faq",
  HTTP_HOOK = "http-hook",
  BUTTON = "button",
  TRANSBOARD = "TRANSBOARD",
  CONVERSATION_ASSIGNED = "conversation-assigned",
  PRINT_SCREEN = "print-screen",
  END_CONVERSATION = "end-conversation",
  RATING = "rating",
  TEMPLATE = "template",
  COMMENT = "comment",
  CAMPAIGN_EVENT = "campaign-event",
  REASSIGN_CONVERSATION = "reassign-conversation",
  PRIVACY_POLICY = "privacy-policy",

  //Hospitality Responses
  ROOM_RATE = "room-rate",
  OMNIBEES_RESERVATION = "omnibees-reservation",
  HOTEL_INFO = "hotel-info",
  BOOKING_CONFIRMATION = "booking-confirmation",
  BOOKING_CANCELLATION = "booking-cancellation",

  //Qualirede Responses
  GUIA_MEDICO = "guia-medico",
  STATUS_GUIA = "status-guia",
  GUIA_BENEFICIARIO = "guia-beneficiario",
  QR_BUSCA_FORNECEDOR = "qr-busca-fornecedor",
  QR_BUSCA_ID = "qr-busca-id",

  //manager system
  MEDICAL_APPOINTMENT = "medical-appointment",
  MNGS_CHECK_ACCOUNT = "mngs-check-account",
  MNGS_ACCOUNT = "mngs-account",
  MNGS_CHECK_APPOINTMENT = "mngs-check-appointment",

  //manager system
  NETPACS_MEDICAL_APPOINTMENT = "netpacs-medical-appointment",

  //manager system
  BOT_DESIGNER_MEDICAL_APPOINTMENT = "botdesigner-medical-appointment",
  BOT_DESIGNER_APPOINTMENT_RESCHEDULE = "botdesigner-appointment-reschedule",
  BOT_DESIGNER_PATIENT_CREATION = "botdesigner-patient-creation",
  BOT_DESIGNER_CHECK_ACCOUNT = "botdesigner-check-account",
  BOT_DESIGNER_MEDICAL_SCALE = "medical-scale-botdesigner",

  //bd health
  BOT_DESIGNER_APPOINTMENT_LIST = "botdesigner-appointment-list",
  BOT_DESIGNER_RESET_MEDICAL_APPOINTMENT = "botdesigner-reset-medical-appointment",
  BOT_DESIGNER_REASON_FOR_NOT_SCHEDULING = "botdesigner-reason-for-not-scheduling",
  BOT_DESIGNER_PATIENT_IDENTIFICATION = "botdesigner-patient-identification",

  BOT_DESIGNER_CONFIRMATION = "botdesigner-confirmation",
  BOT_DESIGNER_CHECK_DOCTOR = "botdesigner-check-doctor",
  BOT_DESIGNER_DOCTOR_SCHEDULES = "botdesigner-doctor-schedules",
  BOT_DESIGNER_RECOVER_PASSWORD = "botdesigner-recover-password",

  BOT_DESIGNER_SAP_INTEGRATION = "botdesigner-sap-integration",
}

export type ElementType =
  | IResponseElementWebhook
  | IResponseElementText
  | IResponseElementImage
  | IResponseElementVideo
  | IResponseElementSetAttribute
  | IResponseElementCard
  | IResponseElementQuestion
  | IResponseElementGoto
  | IResponseElementAdaptiveCard
  | IResponseElementRoomRate
  | IResponseElementBookingConfirmation
  | IResponseElementBookingCancellation
  | IResponseElementHotelInfo
  | IResponseElementGuiaMedico
  | IResponseElementStatusGuia
  | IResponseElementGuiasBeneficiario
  | IResponseElementPrintScreen
  | IResponseElementSetRecognizerScope
  | IResponseElementEndConversation
  | IResponseElementRating
  | IResponseElementMedicalAppointment
  | IResponseElementTemplate
  | IResponseElementComment
  | IResponseElementBDMedicalScale
  | IResponseElementBDResetMedicalAppointment
  | IResponseElementReassignConversation
  | IResponseElementPrivacyPolicy
  | IResponseElementBDPatientRecoverPassword;

export interface IFilter {
  operator: OperatorType;
  conditions?: ICondition[];
}

export interface ICondition {
  name?: string;
  operator?: ConditionMethodType;
  value?: string;
  type: ConditionType;
}

export interface IResponse {
  _id: string;
  type: ResponseType;
  elements?: ElementType[];
  typping_delay?: number;
  filter?: IFilter;
}

export interface IPart {
  value: string;
  type?: string;
  name?: string;
  mandatory?: boolean;
}

export interface IParameter {
  name: string;
  type: string;
  typeId?: string;
  mandatory?: boolean;
  defaultValue?: any;
  value?: any;
}

export interface IUserSay {
  recognizer: RecognizerType;
  parts: IPart[];
}

export interface ILanguage {
  language: string;
  responses?: IResponse[];
  userSays?: IUserSay[];
}

export interface IInteractionBase extends BaseModel {
  name: string;
  description?: string;
  isCollapsed: Boolean;
  type: InteractionType;
  parameters?: IParameter[];
  triggers: string[];
  children?: string[];
  path?: string[];
  completePath?: string[];
  botId: string;
  workspaceId: string;
  parentId?: string;
  position: Number | any;
  params?: any;
  comments: IComment[];
  deletedAt: Date;
  reference: string;
}

export interface IComment {
  comment: string;
  userId: string;
  createdAt?: string;
}

export interface IInteraction extends IInteractionBase {
  languages: ILanguage[];
}

export interface IInteractionRecognized extends IInteractionBase {
  responses?: IResponse[];
  userSays?: IUserSay[];
}
